// Apps Script-compatible function to create query params from an object

import { ApiBaseException } from './errors';

// PS. We can't use the standard URLSearchParams because it's not supported in Apps Script
export function createQueryParams(params?: Record<string, unknown>): string {
  if (!params || typeof params !== 'object') return '';

  const parts: string[] = [];

  for (const key in params) {
    if (!Object.prototype.hasOwnProperty.call(params, key)) continue;

    const value = params[key];

    // Handle null and undefined just like URLSearchParams
    if (value === undefined) {
      parts.push(`${key}=`);
      continue;
    }
    if (value === null) {
      parts.push(`${key}=null`);
      continue;
    }

    // Handle arrays like key[]=a&key[]=b
    if (Array.isArray(value)) {
      for (const el of value) {
        if (el === undefined || el === null) continue;
        parts.push(
          `${encodeURIComponent(key)}[]=${encodeURIComponent(String(el))}`,
        );
      }
      continue;
    }

    // Reject nested objects, as these produce `[object Object]`
    if (typeof value === 'object') {
      console.error(
        `createQueryParams: nested objects are not supported (key: ${key}), skipping...`,
      );
      continue;
    }

    // Primitive values
    parts.push(
      `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    );
  }

  return parts.length ? `?${parts.join('&')}` : '';
}

/**
 * Catch 404 errors and return null instead of throwing an error.
 * @param fn - The function to execute.
 * @returns The result of the function or null if the function throws a 404 error.
 */
export async function catch404ReturnNull<T>(
  fn: () => Promise<T>,
): Promise<T | null> {
  try {
    return await fn();
  } catch (error) {
    if (error instanceof ApiBaseException && error.status === 404) {
      return null;
    }
    throw error;
  }
}
